// client/src/components/transportadores/Transportadores.js
import React, { Fragment, useState, useCallback } from 'react';
import { useTransportadores, useMutateTransportadores } from '../../hooks/useTransportadores';
import TransportadorForm from './TransportadorForm';
import TransportadorRow from './TransportadorRow';
import FiltroTransportadores from './FiltroTransportadores';
import { PlusCircle, Filter as FilterIcon, Loader2, Truck, XCircle, SquarePen, Trash2 } from 'lucide-react';
import Pagination from '../common/Pagination';
import PageHeader from '../layout/PageHeader';
import FabButton from '../common/FabButton';
import ActionsDrawer from '../common/ActionsDrawer';

const Transportadores = () => {
    const [showForm, setShowForm] = useState(false);
    const [showFilters, setShowFilters] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [filters, setFilters] = useState({});
    const [editingTransportador, setEditingTransportador] = useState(null);
    const [selectedTransportador, setSelectedTransportador] = useState(null);

    const itemsPerPage = 10;

    const { data, isLoading: loading } = useTransportadores({
        page: currentPage,
        limit: itemsPerPage,
        ...filters
    });

    const transportadores = data?.data || [];
    const count = data?.count || 0;

    const { deleteTransportador, isPending } = useMutateTransportadores();

    const handleAdicionarClick = useCallback(() => {
        setEditingTransportador(null);
        setShowForm(true);
        setShowFilters(false);
    }, []);

    const handleEditTransportador = useCallback((transportador) => {
        setEditingTransportador(transportador);
        setShowForm(true);
        setShowFilters(false);
        setSelectedTransportador(null);
        window.scrollTo({ top: 0, behavior: 'smooth' });
    }, []);

    const handleDeleteTransportador = useCallback(async (id, nome) => {
        if (window.confirm(`Tem certeza que deseja deletar o transportador: ${nome}?`)) {
            try {
                await deleteTransportador.mutateAsync(id);
                setSelectedTransportador(null);
            } catch (error) {
                console.error('Erro ao deletar transportador:', error);
            }
        }
    }, [deleteTransportador]);

    const handleCloseForm = useCallback(() => {
        setShowForm(false);
        setEditingTransportador(null);
    }, []);

    const handleUpdateFilters = useCallback((newFilters) => {
        setFilters(newFilters);
        setCurrentPage(1);
    }, []);

    const getDisplayId = (id) => id ? id.toString().slice(-6).toUpperCase() : 'N/D';

    const paginate = useCallback((page) => {
        if (page !== currentPage) {
            setCurrentPage(page);
            window.scrollTo({ top: 0, behavior: 'smooth' });
        }
    }, [currentPage]);

    const hasFilters = Object.keys(filters).length > 0;

    return (
        <Fragment>
            <div className="page-management-container">
                <PageHeader
                    icon={<Truck />}
                    title="Gerenciamento de Transportadores"
                    subtitle={`${count} transportador(es)`}
                    actions={<>
                        {!showForm && (
                            <>
                                <button
                                    onClick={() => setShowFilters(p => !p)}
                                    className={`action-button ${showFilters ? 'action-button-primary' : 'action-button-secondary'}`}
                                >
                                    <FilterIcon size={18} className="icon" />
                                    {showFilters ? 'Ocultar Filtros' : 'Filtros'}
                                </button>
                            </>
                        )}
                        {showForm && (
                            <button onClick={handleCloseForm} className="action-button action-button-secondary">
                                <XCircle size={18} className="icon" /> Fechar
                            </button>
                        )}
                    </>}
                />

                {showFilters && !showForm && (
                    <div className="content-panel">
                        <div className="panel-header">
                            <h3 className="panel-title">Filtrar Transportadores</h3>
                        </div>
                        <FiltroTransportadores
                            onApplyFilters={handleUpdateFilters}
                            activeFilters={filters}
                            loading={loading}
                        />
                    </div>
                )}

                {showForm && (
                    <div className="content-panel">
                        <TransportadorForm current={editingTransportador} onCloseForm={handleCloseForm} onSaveSuccess={handleCloseForm} />
                    </div>
                )}
            </div>

            {!showForm && (
                <div className="table-container">
                    <div className="table-wrapper">
                        {loading && transportadores.length === 0 ? (
                            <div className="table-loading-state">
                                <Loader2 size={24} className="animate-spin" />
                                <span>Carregando transportadores...</span>
                            </div>
                        ) : transportadores.length > 0 ? (
                            <table className="styled-table">
                                <thead>
                                    <tr>
                                        <th>ID</th>
                                        <th>Nome / Razão Social</th>
                                        <th>CPF / CNPJ</th>
                                        <th>Telefone</th>
                                        <th>Motoristas</th>
                                        <th>Veículos</th>
                                        <th className="text-right">Ações</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {transportadores.map(t => (
                                        <TransportadorRow
                                            key={t._id}
                                            transportador={t}
                                            displayId={getDisplayId(t._id)}
                                            onOpenActions={setSelectedTransportador}
                                            loading={isPending}
                                        />
                                    ))}
                                </tbody>
                            </table>
                        ) : (
                            <div className="table-empty-state">
                                <Truck size={48} />
                                <h3>Nenhum transportador encontrado</h3>
                                <p>
                                    {hasFilters
                                        ? 'Nenhum transportador corresponde aos filtros aplicados.'
                                        : 'Clique em "Adicionar Transportador" para cadastrar o primeiro.'}
                                </p>
                            </div>
                        )}
                    </div>
                    <Pagination
                        totalItems={count}
                        itemsPerPage={itemsPerPage}
                        currentPage={currentPage}
                        onPageChange={paginate}
                    />
                </div>
            )}

            <FabButton
                onClick={handleAdicionarClick}
                title="Adicionar Transportador"
                visible={!showForm}
                icon={<Truck size={26} className="icon" />}
            />

            <ActionsDrawer
                open={!!selectedTransportador}
                onClose={() => setSelectedTransportador(null)}
                title={
                    selectedTransportador
                        ? (selectedTransportador.tipoPessoa === 'Física'
                            ? selectedTransportador.nomeCompleto
                            : (selectedTransportador.nomeFantasia || selectedTransportador.razaoSocial)) || 'Transportador'
                        : ''
                }
                actions={selectedTransportador ? [
                    {
                        key: 'editar',
                        label: 'Editar',
                        icon: <SquarePen size={20} className="icon" />,
                        onClick: () => handleEditTransportador(selectedTransportador),
                    },
                    {
                        key: 'excluir',
                        label: 'Excluir',
                        icon: <Trash2 size={20} className="icon" />,
                        onClick: () => handleDeleteTransportador(
                            selectedTransportador._id,
                            (selectedTransportador.tipoPessoa === 'Física' ? selectedTransportador.nomeCompleto : (selectedTransportador.nomeFantasia || selectedTransportador.razaoSocial)) || 'este transportador'
                        ),
                        destructive: true,
                        loading: isPending,
                    },
                ] : []}
            />
        </Fragment>
    );
};

export default Transportadores;
